import React, { FC } from 'react';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { SummaryItem } from '../../../../components/summary-list/index';
import { Tooltip } from '../../../../components/tooltip/index';
import { b2cStakeholderTypes, stakeholderTypeTitle } from '../../../../types/stakeholder.types';
import { openCompanyStorageUrlInNewTab } from '../../../../utils/company-storage-url.util';
import { ChildrenFormProps } from '../stakeholder-actions.types';

export type FinalStepProps = ChildrenFormProps;

export const FinalStep: FC<FinalStepProps> = ({
  handleCloseModal,
  nextFormStep,
  formData,
  prevFormStep,
  isLoading,
  stakeholder,
}) => {
  const { stepOne, stepTwo } = formData();
  const companyId = stakeholder?.company?.id;

  const handleDocumentClick = (link: string) => {
    if (!companyId) return;
    void openCompanyStorageUrlInNewTab(companyId, link);
  };

  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-4 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
          <span className="px-4 text-sm font-[550] text-gray-700">
            <AppFormattedMessage id="GENERAL" />
          </span>
          <div className="flex flex-col divide-y divide-gray-100 rounded-b-lg border border-gray-100 bg-white p-4 pb-3">
            <SummaryItem
              className="pt-0"
              title={<AppFormattedMessage id="FULL_NAME" />}
              value={<Tooltip content={stepOne?.fullName}> {stepOne?.fullName}</Tooltip>}
            />
            <div className="flex flex-col gap-3 pb-3">
              <SummaryItem
                className="pb-0"
                title={<AppFormattedMessage id="EMAIL_MAIN" />}
                value={stepOne.email}
              />
              {stepOne.subEmails.map(({ value: subEmail }) => (
                <SummaryItem
                  className="py-0"
                  key={subEmail}
                  title={<AppFormattedMessage id="SUB_EMAIL_PLACEHOLDER" />}
                  value={subEmail}
                />
              ))}
            </div>
            <SummaryItem
              title={<AppFormattedMessage id="STAKEHOLDER_TYPE" />}
              value={<AppFormattedMessage id={stakeholderTypeTitle[stepOne.stakeholderType]} />}
            />

            {b2cStakeholderTypes.includes(stepOne.stakeholderType) ? (
              <>
                <SummaryItem
                  title={<AppFormattedMessage id="COUNTRY_OF_COMPANY" />}
                  value={stepOne.company?.incorporationCountry?.name || '-'}
                />
                <SummaryItem
                  title={<AppFormattedMessage id="COMPANY_IDENTIFIER" />}
                  value={stepOne.company?.identifier || '-'}
                />
                <SummaryItem
                  title={<AppFormattedMessage id="DATE_OF_INCORPORATION_OPTIONAL" />}
                  value={
                    stepOne.company?.incorporationDate
                      ? `${stepOne.company?.incorporationDate?.day}/${stepOne.company?.incorporationDate?.month + 1}/${stepOne.company?.incorporationDate?.year}`
                      : '-'
                  }
                />
              </>
            ) : (
              <>
                <SummaryItem
                  title={<AppFormattedMessage id="PASSPORT_NO" />}
                  value={stepOne.human?.passportNo || '-'}
                />
                <SummaryItem
                  title={<AppFormattedMessage id="PASSPORT_EXPIRY_DATE" />}
                  value={
                    stepOne.human?.passportExpiryDate
                      ? `${stepOne.human?.passportExpiryDate?.day}/${stepOne.human?.passportExpiryDate?.month + 1}/${stepOne.human?.passportExpiryDate?.year}`
                      : '-'
                  }
                />
                <SummaryItem
                  title={<AppFormattedMessage id="NATIONALITY" />}
                  value={stepOne.human?.nationality?.name || '-'}
                />
                <SummaryItem
                  title={<AppFormattedMessage id="BIRTHDAY" />}
                  value={
                    stepOne.human?.birthdayDate
                      ? `${stepOne.human?.birthdayDate?.day}/${stepOne.human?.birthdayDate?.month + 1}/${stepOne.human?.birthdayDate?.year}`
                      : '-'
                  }
                />
              </>
            )}
            <SummaryItem
              title={<AppFormattedMessage id="PHONE_NUMBER" />}
              value={
                stepOne.phoneCountry && stepOne.phoneNumber
                  ? `${stepOne.phoneCountry.prefix} ${stepOne.phoneNumber}`
                  : '-'
              }
            />
            <SummaryItem
              className="pb-0"
              title={<AppFormattedMessage id="ADDRESS" />}
              value={stepOne.address}
            />
          </div>
        </div>
        <div className="flex w-full flex-col rounded-lg border border-gray-100 bg-white">
          <div className={'flex gap-3 text-nowrap px-4 py-2'}>
            <span className="w-[120px] flex-shrink-0 text-xs font-[450] text-gray-500">
              <AppFormattedMessage id="DOCUMENTS" />:
            </span>
            <div className="flex max-w-[220px] flex-col gap-2">
              {(stepTwo?.files?.length || 0) > 0 ? (
                stepTwo?.files?.map(({ doc, docLink }) => (
                  <div
                    className="cursor-pointer truncate text-xs font-[450] text-brand-700 underline"
                    key={docLink}
                    onClick={() => handleDocumentClick(docLink)}
                  >
                    {doc?.name}
                  </div>
                ))
              ) : (
                <span className="truncate text-xs font-[450] text-gray-700">-</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex w-full flex-col rounded-lg border border-gray-100 bg-white">
          <div className="flex w-full gap-3 px-4 py-2">
            <span className="w-[120px] flex-shrink-0 text-xs font-[450] text-gray-500">
              {<AppFormattedMessage id="ADDITIONAL_NOTES" />}:
            </span>
            <div className="max-w-[250px] break-words text-xs font-[450] text-gray-700">
              {stepTwo?.additionalNotes || '-'}
            </div>
          </div>
        </div>
      </div>

      <div className="flex h-9 w-full justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
          type="button"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          <Button
            className="h-full w-fit rounded border border-gray-100 bg-white px-3 py-[6px] text-sm font-[450] text-gray-700 shadow-xs"
            onClick={prevFormStep}
            styleType="NONE"
            type="button"
          >
            <AppFormattedMessage id="BACK" />
          </Button>

          <Button
            className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
            isLoading={isLoading}
            onClick={nextFormStep}
            type="button"
          >
            <AppFormattedMessage id="ADD_STAKEHOLDER" />
          </Button>
        </div>
      </div>
    </>
  );
};
